<script setup>
import axios from 'axios'
import { Plus, Star, Variable } from 'lucide-vue-next'

import ReviewVariablesList from '../Components/ReviewVariablesList.vue'

const props = defineProps({
  review: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['templateSelected', 'variableUsed'])
const router = useRouter()

const templates = ref([])
const displayVariablesList = ref(false)

onMounted(() => {
  getTemplates()
})

const emitVariableUsed = (text) => {
  emit('variableUsed', text)
}

const redirectToAddTemplate = () => {
  return router.push({ name: 'ReviewSettings', query: { addTemplate: true }, hash: '#templates' })
}

const getTemplateRoute = () => {
  return `${router.resolve({ name: 'ReviewSettings' }).path}#templates`
}

const getReviewSlug = (rating) => {
  if (rating >= 0 && rating <= 2) {
    return 'negative'
  } else if (rating === 3) {
    return 'neutral'
  }
  return 'positive'
}

const getTemplates = () => {
  axios
    .get('/reviews-reply-templates', {
      params: {
        isFavorite: 1
      }
    })
    .then((response) => {
      const slug = getReviewSlug(props.review.rating)
      templates.value = response.data.data.filter((template) => template.reviewReplyTemplateMood.slug === slug)
    })
}
</script>

<template>
  <div class="flex w-full justify-between gap-4 xl:w-1/2">
    <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
      <AppTooltip v-for="template in templates" :key="template.id" tooltip-position="top" width="w-80">
        <Button variant="outline" size="icon" class="text-lg" @click="$emit('templateSelected', template)">
          {{ template.emoji }}
        </Button>
        <template #tooltip-content>
          <div class="ml-1">
            <h2 class="mb-0.5 text-left font-semibold">
              {{ template.title }}
            </h2>
            <p class="pb-0.5 text-left text-xs">
              {{ template.content }}
            </p>
          </div>
        </template>
      </AppTooltip>
      <AppTooltip tooltip-position="top">
        <Button variant="outline" size="icon" class="text-primary" @click="redirectToAddTemplate()">
          <Plus />
        </Button>
        <template #tooltip-content>
          <h2>
            {{ $t('pages.review.reviewPage.items.addTemplate') }}
          </h2>
        </template>
      </AppTooltip>
      <Button variant="link" as-child>
        <router-link :to="getTemplateRoute()">
          <Star class="fill-primary" />
          {{ $t('pages.review.reviewPage.items.buttons.myTemplates') }}
        </router-link>
      </Button>
      <span class="text-sm text-muted-foreground">
        {{
          $t('pages.review.reviewPage.templateToolTip', {
            mood: $t(`pages.review.reviewPage.variables.mood.${getReviewSlug(review.rating)}`)
          })
        }}
      </span>
    </div>
    <div>
      <AppTooltip tooltip-position="top">
        <Button
          variant="outline"
          size="icon"
          :class="displayVariablesList ? 'text-muted-foreground' : 'text-primary'"
          @click="displayVariablesList = !displayVariablesList"
        >
          <Variable />
        </Button>
        <template #tooltip-content>
          <h2>
            {{
              displayVariablesList
                ? $t('pages.review.reviewPage.items.hideAvailableVariables')
                : $t('pages.review.reviewPage.items.availableVariables')
            }}
          </h2>
        </template>
      </AppTooltip>
    </div>
  </div>
  <ReviewVariablesList v-if="displayVariablesList" class="mt-6 w-full lg:w-1/2" @variable-used="emitVariableUsed" />
</template>
